import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api';
import Drawer from '../UI/Drawer';
import { primaryBtn, primaryBtnStyle, ghostBtn, ghostBtnStyle, inputCls, inputStyle, fieldLabel, fieldLabelStyle, errMessage } from '../UI/ink';
import { SevChip } from './AlertDetail';

const PRIORITIES = [['critical', 'Critical'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']];
const PRIORITY_FROM_SEV = { crit: 'critical', high: 'high', med: 'medium', low: 'low', unk: 'medium' };
const RANK = { crit: 0, high: 1, med: 2, low: 3, unk: 4 };

// Opens a case with the chosen alerts linked, then goes to it.
const CreateCaseDrawer = ({ open, items, onClose }) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !items.length) return;
    const worst = items.slice().sort((a, b) => RANK[a.sev] - RANK[b.sev])[0];
    const hosts = [...new Set(items.map(i => i.host))];
    setTitle(items.length === 1 ? `${items[0].title} on ${items[0].host}` : `${items.length} alerts on ${hosts.slice(0, 2).join(', ')}${hosts.length > 2 ? ` +${hosts.length - 2}` : ''}`);
    setPriority(PRIORITY_FROM_SEV[worst.sev]);
    setDescription('');
    setError(null);
  }, [open, items]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim()) { setError('Title is required.'); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient.post('/api/cases', {
        title: title.trim(),
        description: description.trim() || null,
        priority,
        alert_ids: items.map(i => ({ type: i.kind, id: i.id })),
      });
      onClose();
      if (res.data?.id) navigate(`/cases/${res.data.id}`);
    } catch (err) {
      setError(errMessage(err, 'Failed to create case.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      kicker="CASES"
      title="Create case"
      subtitle={`Links ${items.length} alert${items.length === 1 ? '' : 's'} to a new case so the investigation is tracked in one place.`}
      footer={(
        <>
          <span />
          <div className="flex gap-2.5">
            <button type="button" onClick={onClose} className={ghostBtn} style={ghostBtnStyle}>Cancel</button>
            <button type="submit" form="create-case-form" disabled={busy} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Creating…' : 'Create case'}</button>
          </div>
        </>
      )}
    >
      <form id="create-case-form" onSubmit={submit} className="flex flex-col gap-5">
        {error && <div role="alert" className="px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>{error}</div>}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="case-title" className={fieldLabel} style={fieldLabelStyle}>Title</label>
          <input id="case-title" type="text" required value={title} onChange={e => setTitle(e.target.value)} className={inputCls} style={inputStyle} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="case-priority" className={fieldLabel} style={fieldLabelStyle}>Priority</label>
          <select id="case-priority" value={priority} onChange={e => setPriority(e.target.value)} className={inputCls} style={inputStyle}>
            {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="case-desc" className={fieldLabel} style={fieldLabelStyle}>Notes <span className="font-normal" style={{ color: 'var(--ink-faint)' }}>(optional)</span></label>
          <textarea id="case-desc" rows={3} value={description} onChange={e => setDescription(e.target.value)} className="box-border px-3 py-2.5 rounded-[10px] border text-sm resize-none outline-none" style={inputStyle} />
        </div>
        <div className="flex flex-col gap-2">
          <span className={fieldLabel} style={fieldLabelStyle}>Linked alerts</span>
          <div className="flex flex-col rounded-[10px] border overflow-hidden" style={{ borderColor: 'var(--surf-line)' }}>
            {items.map(i => (
              <div key={i.key} className="flex items-center gap-3 px-3.5 py-2.5 border-b last:border-b-0 min-w-0" style={{ borderColor: 'var(--surf-line)', background: 'var(--surf-bg)' }}>
                <SevChip sev={i.sev} />
                <span className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-[13px] truncate">{i.title}</span>
                  <span className="font-mono text-[11px] truncate" style={{ color: 'var(--ink-faint)' }}>{i.host}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </form>
    </Drawer>
  );
};

export default CreateCaseDrawer;
